import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchKanjiDetail } from "@/lib/api";
import { TEST_IDS } from "@/constants/testIds";
import MnemonicEditor from "@/components/MnemonicEditor";
import JlptBadge, { JLPT_COLOR } from "@/components/JlptBadge";
import ProgressButtons from "@/components/ProgressButtons";
import PrereqSuggestions from "@/components/PrereqSuggestions";
import KanjiGlyph from "@/components/KanjiGlyph";
import KanaOriginChip from "@/components/KanaOriginChip";
import KanaWithOrigins from "@/components/KanaWithOrigins";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

function PartChip({ part, onSelect, isPhonetic = false }) {
  const accent = isPhonetic
    ? "#C84B31"
    : part.is_kanji && part.jlpt
      ? JLPT_COLOR[part.jlpt]
      : "#2A4B6A";
  // Suffix non-kanji parts with "radical" when meaning doesn't already say so
  const m = part.meaning || "";
  const labelText = part.is_kanji
    ? m
    : m
      ? (m.toLowerCase().includes("radical") ? m : `${m} radical`)
      : "radical";
  return (
    <button
      data-testid={TEST_IDS.focusComponent(part.char)}
      onClick={() => part.is_kanji && onSelect?.(part.char)}
      className={`flex items-center gap-2 border px-3 py-2 transition-colors ${
        part.is_kanji ? "hover:border-shu cursor-pointer" : "cursor-default"
      }`}
      style={{
        borderColor: accent,
        background: isPhonetic ? "rgba(200,75,49,0.06)" : "#F4F1EB",
      }}
      title={isPhonetic ? "phonetic component" : labelText}
    >
      <span
        className="font-serif text-3xl leading-none"
        style={{ color: isPhonetic ? accent : !part.is_kanji ? accent : "#1E1B18" }}
      >
        {part.char}
      </span>
      <div className="flex flex-col items-start">
        <span className="text-[10px] uppercase tracking-wider text-sumiSoft font-sans">
          {labelText}
        </span>
        {isPhonetic && (
          <span className="text-[9px] uppercase tracking-widest text-shu font-sans mt-0.5">
            phonetic clue
          </span>
        )}
      </div>
    </button>
  );
}

function Section({ label, children }) {
  return (
    <div className="border-b border-borderDefault py-4 px-4 md:px-7 last:border-b-0">
      <p className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans mb-2.5">
        {label}
      </p>
      {children}
    </div>
  );
}

function ProgressButtons_DELETED() {} // placeholder removed

export default function KanjiCard({ kanji, onSelectKanji, onJumpToPhonetic }) {
  const detailQ = useQuery({
    queryKey: ["kanji", kanji],
    queryFn: () => fetchKanjiDetail(kanji),
    enabled: !!kanji,
  });

  if (!kanji || !detailQ.data) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sumiSoft font-sans uppercase tracking-[0.3em] text-xs">
          loading…
        </p>
      </div>
    );
  }

  const k = detailQ.data;

  // collect phonetic components (the actual driver chars from /api/kanji)
  const phoneticComps = new Set((k.phonetic_series || []).map((s) => s.component));

  return (
    <div className="h-full flex flex-col">
      {/* hero — kept OUTSIDE focus-card-info so the demo's slide 4
          spotlight doesn't cover the animated KanjiGlyph. The demo
          narration focuses on the facts (meaning, readings, strokes,
          usage), not the animation. */}
      {/* hero */}
      <div className="px-4 md:px-7 pt-4 pb-4 flex items-end gap-4 border-b border-borderDefault">
        <div data-testid={TEST_IDS.focusKanji} className="shrink-0 select-text">
          <KanjiGlyph ch={k.kanji} size={128} />
        </div>
        <div className="flex flex-col gap-1.5 pb-2 min-w-0">
          <JlptBadge level={k.jlpt} jouyou={k.jouyou} size="md" className="self-start" />
          <p data-testid={TEST_IDS.focusMeaning} className="font-serif text-2xl md:text-3xl text-sumi leading-tight">
            {k.meaning}
          </p>
          {k.meanings?.length > 1 && (
            <p className="font-sans text-xs text-sumiSoft tracking-wide truncate">
              {k.meanings.slice(1).join(" · ")}
            </p>
          )}
        </div>
      </div>

      {/* facts — wrapped together so the demo can spotlight the readings,
          stroke count and usage as one rect without including the
          animated glyph above. */}
      <div data-testid="focus-card-info">

      {/* progress */}
      <div className="px-4 md:px-7 py-3 border-b border-borderDefault">
        <ProgressButtons kind="kanji" id={k.kanji} testIdPrefix="progress" />
        <PrereqSuggestions constituents={k.components_detail} own={k.kanji} />
      </div>

      <div className="grid grid-cols-2 border-b border-borderDefault">
        <div className="py-3 px-4 md:px-7 border-r border-borderDefault" data-testid={TEST_IDS.focusOn}>
          <p className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans mb-1.5">on&apos;yomi</p>
          <p className="font-serif text-lg md:text-xl text-sumi leading-snug break-words">
            {k.on_readings?.length ? (
              <KanaWithOrigins text={k.on_readings.join("、")} />
            ) : "—"}
          </p>
        </div>
        <div className="py-3 px-4 md:px-7" data-testid={TEST_IDS.focusKun}>
          <p className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans mb-1.5">kun&apos;yomi</p>
          <p className="font-serif text-lg md:text-xl text-sumi leading-snug break-words">
            {k.kun_readings?.length ? k.kun_readings.join("、") : "—"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 border-b border-borderDefault">
        <div className="py-3 px-4 md:px-7 border-r border-borderDefault">
          <p className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans mb-1.5">strokes</p>
          <p className="font-serif text-xl md:text-2xl text-sumi">{k.strokes ?? "—"}</p>
        </div>
        <div className="py-3 px-4 md:px-7">
          <p className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans mb-1.5">
            usage
          </p>
          <p className="font-serif text-xl md:text-2xl text-sumi leading-tight">
            {k.freq_pct != null ? `${k.freq_pct}%` : "—"}
          </p>
          {k.freq_band && (
            <p className="font-sans text-[10px] uppercase tracking-widest text-sumiSoft mt-0.5">
              {k.freq_band}
            </p>
          )}
        </div>
      </div>
      </div>{/* /focus-card-info */}

      <Section label="built from">
        <div data-testid={TEST_IDS.focusBuiltFrom}>
          {k.components_detail?.length ? (
            <div className="flex flex-wrap gap-2">
              {k.components_detail.map((p) => (
                <PartChip
                  key={p.char}
                  part={p}
                  onSelect={onSelectKanji}
                  isPhonetic={phoneticComps.has(p.char)}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-sumiSoft font-sans italic">primitive — a foundational shape</p>
          )}
        </div>
      </Section>

      {k.kana_origin && <KanaOriginChip info={k.kana_origin} />}

      {k.phonetic_series?.length > 0 && (
        <Section label="phonetic series">
          <div data-testid={TEST_IDS.focusPhonetic} className="space-y-1">
            {k.phonetic_series.map((s) => (
              <div
                key={`${s.component}-${s.reading}`}
                className="flex items-center gap-2 px-2 py-1 -mx-2 border border-transparent hover:border-shu/40 hover:bg-shu/5 transition-colors"
              >
                <button
                  type="button"
                  onClick={() => onJumpToPhonetic?.()}
                  className="flex-1 text-left font-sans text-sm text-sumi leading-snug"
                >
                  <span className="font-serif text-lg text-shu mr-2">{s.component}</span>
                  drives reading{" "}
                  <span className="font-serif text-lg text-shu mr-2">{s.reading}</span>
                  <span className="text-xs text-sumiSoft uppercase tracking-widest ml-1">
                    in {s.count} kanji →
                  </span>
                </button>
                {s.is_ghost_driver && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid={`phonetic-ghost-badge-${s.component}`}
                        className="text-[9px] uppercase tracking-[0.18em] text-sumiSoft border border-borderSoft px-1.5 py-px cursor-help hover:bg-shu/10 hover:text-shu hover:border-shu/40 transition-colors shrink-0"
                      >
                        etymological
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="end"
                      sideOffset={6}
                      className="w-80 bg-washi border-borderDefault text-sumi p-4"
                    >
                      <p className="font-serif text-base text-sumi mb-2">
                        Why isn&apos;t <span className="text-shu text-lg">{s.component}</span> in &lsquo;built from&rsquo;?
                      </p>
                      <p className="font-sans text-xs text-sumiSoft leading-relaxed mb-2">
                        <span className="text-sumi font-serif text-sm">{s.component}</span> is a{" "}
                        <strong>historical phonetic driver</strong> — the original 形声
                        (<em>keisei</em>) component that gave this family its{" "}
                        <span className="text-shu font-serif text-sm">{s.reading}</span> reading
                        thousands of years ago.
                      </p>
                      <p className="font-sans text-xs text-sumiSoft leading-relaxed mb-3">
                        It isn&apos;t a standard modern kanji, so the visual decomposition above
                        doesn&apos;t include it. The clue is etymological, not graphical — but it
                        still predicts the reading reliably across all {s.count} kanji in the family.
                      </p>
                      <Link
                        to="/atlas"
                        className="font-sans text-[10px] uppercase tracking-[0.25em] text-shu border border-shu/50 px-2 py-1 hover:bg-shu hover:text-washi transition-colors inline-block"
                        data-testid="ghost-popover-atlas-link"
                      >
                        browse the phonetic atlas →
                      </Link>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section label="mnemonic">
        <MnemonicEditor key={k.kanji} kanji={k.kanji} />
      </Section>
    </div>
  );
}
